import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Camera, Eye, EyeOff, MapPinned, ReceiptText } from 'lucide-react';
import { ApiError } from '../api/client';
import { Alert, Button, Input, LanguageSwitch, Logo } from '../components';
import { getLastUser, useAuth } from './AuthContext';

/**
 * Shared login screen: brand panel on the left (desktop), form on the right; stacked on phones.
 * With `pinEnabled` (operator app) it offers PIN login for the last user on this device.
 */
interface DemoAccount { key: 'admin' | 'partner' | 'washer'; base: string; id: string; pwd: string }

const DEMO_APPS = [['admin', '/admin/'], ['partner', '/partner/'], ['washer', '/app/']] as const;
// Demo builds only: VITE_DEMO_ACCOUNTS = "adminEmail:password,partnerEmail:password,washerEmail:password" (in that order).
// Kept out of the source so live builds, and their source maps, never contain demo passwords.
const DEMO_ACCOUNTS: DemoAccount[] = ((import.meta.env.VITE_DEMO_ACCOUNTS as string | undefined) ?? '').split(',').filter(Boolean).slice(0, 3)
  .map((a, i) => ({ key: DEMO_APPS[i][0], base: DEMO_APPS[i][1], id: a.slice(0, a.indexOf(':')), pwd: a.slice(a.indexOf(':') + 1) }));

export function LoginPage({ pinEnabled = false, subtitle, demoKey }: { pinEnabled?: boolean; subtitle?: string; demoKey?: DemoAccount['key'] }) {
  const { t } = useTranslation();
  const { login, pinLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation() as { state?: { from?: { pathname?: string } } };
  const from = location.state?.from?.pathname ?? '/';

  const lastUser = pinEnabled ? getLastUser() : null;
  const [mode, setMode] = useState<'password' | 'pin'>(lastUser?.hasPin ? 'pin' : 'password');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Demo builds only (VITE_DEMO_ACCOUNTS): a pill fills in that demo account, the user presses Log in.
  // `?as=<key>` is how a pill on another app's login page hands over.
  const signInAs = (d: DemoAccount) => {
    if (d.key !== demoKey) { window.location.assign(`${d.base}login?as=${d.key}`); return; }
    setMode('password'); setIdentifier(d.id); setPassword(d.pwd);
  };
  useEffect(() => {
    const as = DEMO_ACCOUNTS.find((d) => d.key === demoKey && d.key === new URLSearchParams(window.location.search).get('as'));
    if (as) signInAs(as);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void signIn(identifier, password);
  };

  const signIn = async (id: string, pwd: string) => {
    setError(null);
    setBusy(true);
    try {
      if (mode === 'pin' && lastUser && !pwd) await pinLogin(lastUser.id, pin);
      else await login(id, pwd);
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message.toLowerCase().includes('locked') ? t('auth.locked') : err.status === 401 ? t('auth.invalid_credentials') : err.message);
      } else if ((err as { code?: string })?.code === 'wrong_app') {
        setError((err as Error).message);
      } else {
        setError(t('common.error_generic'));
      }
    } finally {
      setBusy(false);
    }
  };

  const points = [
    { icon: <Camera size={20} />, text: t('auth.hero_point_photos') },
    { icon: <MapPinned size={20} />, text: t('auth.hero_point_tracking') },
    { icon: <ReceiptText size={20} />, text: t('auth.hero_point_billing') },
  ];

  return (
    <div className="gr-login">
      <aside className="gr-login__hero" aria-hidden>
        <Logo size={36} />
        <div className="gr-login__hero-body">
          <span className="gr-login__eyebrow">{t('auth.hero_eyebrow')}</span>
          <h2 className="gr-login__headline">{t('auth.hero_title')}</h2>
          <p className="gr-login__lead">{t('auth.hero_text')}</p>
          <ul className="gr-login__points">
            {points.map((p, i) => <li key={i}><span className="gr-login__point-icon">{p.icon}</span>{p.text}</li>)}
          </ul>
        </div>
        <small className="gr-login__foot">© {new Date().getFullYear()} GreenRinse</small>
      </aside>

      <main className="gr-login__panel">
        <div className="gr-login__top">
          <span className="gr-login__mobile-logo"><Logo size={30} /></span>
          <span className="gr-topbar__spacer" />
          <LanguageSwitch />
        </div>
        <div className="gr-login__form-wrap">
          {subtitle && <span className="gr-login__app">{subtitle}</span>}
          <h1 className="gr-login__title">{t('auth.login_title')}</h1>
          <p className="gr-muted gr-login__sub">{mode === 'pin' && lastUser ? t('auth.logged_in_as', { name: lastUser.fullName }) : t('auth.login_sub')}</p>
          {error && <div style={{ marginBottom: 16 }}><Alert tone="error">{error}</Alert></div>}
          {DEMO_ACCOUNTS.length > 0 && (
            <div className="gr-login__demo">
              <span className="gr-login__demo-title">{t('auth.demo_title')}</span>
              <div className="gr-row">
                {DEMO_ACCOUNTS.map((d) => (
                  <button key={d.key} type="button" data-demo={d.key} disabled={busy}
                    className={`gr-btn ${d.key === demoKey ? 'gr-btn--primary' : 'gr-btn--outline'}`} onClick={() => signInAs(d)}>
                    {t(`auth.demo_${d.key}`)}
                  </button>
                ))}
              </div>
              <small className="gr-muted">{t('auth.demo_hint')}</small>
            </div>
          )}
          <form onSubmit={submit} noValidate>
            {mode === 'pin' && lastUser ? (
              <>
                <Input label={t('auth.pin')} inputMode="numeric" pattern="[0-9]*" autoComplete="one-time-code" maxLength={6}
                  value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} plate autoFocus required />
                <Button type="submit" size="lg" block loading={busy}>{t('auth.login_with_pin')}</Button>
                <Button type="button" variant="ghost" block onClick={() => setMode('password')} style={{ marginTop: 8 }}>{t('auth.use_password')}</Button>
              </>
            ) : (
              <>
                <Input label={t('auth.identifier')} autoComplete="username" inputMode="email" value={identifier} onChange={(e) => setIdentifier(e.target.value)} autoFocus required />
                <div className="gr-field">
                  <label htmlFor="gr-login-password">{t('auth.password')}</label>
                  <div className="gr-input-wrap">
                    <input id="gr-login-password" className="gr-input" type={showPassword ? 'text' : 'password'} autoComplete="current-password"
                      value={password} onChange={(e) => setPassword(e.target.value)} required />
                    <button type="button" className="gr-input-wrap__btn" onClick={() => setShowPassword((s) => !s)}
                      aria-label={showPassword ? t('auth.hide_password') : t('auth.show_password')} aria-pressed={showPassword}>
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <Button type="submit" size="lg" block loading={busy}>{t('auth.login')}</Button>
                {lastUser?.hasPin && (
                  <Button type="button" variant="ghost" block onClick={() => setMode('pin')} style={{ marginTop: 8 }}>{t('auth.login_with_pin')}</Button>
                )}
              </>
            )}
          </form>
        </div>
      </main>
    </div>
  );
}
